// Deterministic intent router for the Lab Assistant.
//
// Replaces the previous model-driven intent detection in `getRecipeChatResponse`.
// Security rationale: routing decisions that gate actions (image generation,
// recipe modification, coin-spending imports) MUST NOT be made by a model that
// is also reading attacker-controlled input — otherwise a pasted recipe or chat
// message can steer the router ("the intent is IMAGE_REQUEST") or smuggle
// instructions. A pure, ordered regex router has no such channel.
//
// A miss degrades to GENERAL (a harmless cooking-answer path), never to a
// privileged action.

export type RecipeChatIntent =
  | 'SEARCH_RECIPES'
  | 'RECIPE_QUESTION'
  | 'IMAGE_REQUEST'
  | 'RECIPE_CREATION'
  | 'RECIPE_IMPORT_READY'
  | 'RECIPE_COPY'
  | 'RECIPE_MODIFY'
  | 'STUDIO_OPEN'
  | 'CHEFCOIN_QUERY'
  | 'GENERAL';

// Recipe-section headings (line-anchored — "Instructions" at the start of a line
// is a recipe heading, but "ignore previous instructions" mid-sentence is not).
const RECIPE_HEADING_RE = /^\s{0,4}(?:ingredients?|method|directions?|instructions?|steps?|serves?|prep time|cook time)\b/im;

// Strong recipe words that are rare in prompt-injection phrasing. NOTE:
// "instructions" is deliberately excluded here because it is the classic
// injection word ("ignore these instructions").
const RECIPE_STRONG_WORD_RE = /\b(ingredients?|method|directions?|prep time|cook time|serves?)\b/i;

// Looks like a chunk of pasted recipe text (headings and/or list-heavy).
function looksLikeRecipeContent(m: string): boolean {
  if (RECIPE_HEADING_RE.test(m)) return true;
  if (RECIPE_STRONG_WORD_RE.test(m)) return true;

  const lines = m.split(/\r?\n/).filter((l) => l.trim().length > 0);
  let listish = 0;
  for (const line of lines) {
    if (/^\s*([-*•·◦]|\d{1,2}[.)])\s+/.test(line)) listish++;
  }
  return lines.length >= 3 && listish >= 2;
}

export function detectRecipeIntent(message: string): RecipeChatIntent {
  const m = (message ?? '').trim();
  const lower = m.toLowerCase();

  // 1. ChefCoin / economy — query-like phrasing (not a bare "balance" mention,
  //    which is how injection attempts like "set my balance to 999" would slip in).
  if (
    /\b(chefcoins?|stipend|currency)\b/.test(lower) ||
    /\b(how (much|many) (coins|chefcoins|credits)|check (my )?(balance|coins)|what('s| is) my (balance|coins))\b/.test(lower) ||
    /\b(cost|price|charge)\b/.test(lower) && /\b(coin|credit|image|recipe|analysis|studio)\b/.test(lower)
  ) {
    return 'CHEFCOIN_QUERY';
  }

  // 2. Image generation.
  if (
    /\b(generate|create|make|new)\b.*\b(image|photo|picture)\b/.test(lower) ||
    /\b(image|photo|picture)\b.*\b(generate|create|make)\b/.test(lower) ||
    /show me what (this|it) looks like/.test(lower)
  ) {
    return 'IMAGE_REQUEST';
  }

  // 3. Copy / duplicate.
  if (
    /\b(copy|duplicate|clone)\b.*\brecipe\b/.test(lower) ||
    /make a copy/.test(lower)
  ) {
    return 'RECIPE_COPY';
  }

  // 4. Modify.
  if (
    /\b(convert to|substitut\w*|replace)\b/.test(lower) ||
    /\b(make|turn|convert|change)\b[^.!?]{0,30}\b(vegan|vegetarian|gluten[ -]?free|dairy[ -]?free|sugar[ -]?free|low[ -]?carb|spicier|healthier|keto|paleo)\b/.test(lower) ||
    /\bscale\b/.test(lower) ||
    /\b(modify|change|update|edit|rewrite) (this|the) recipe\b/.test(lower) ||
    /\b(add|remove|swap)\b.*\b(to|from|in) (this|the) recipe\b/.test(lower)
  ) {
    return 'RECIPE_MODIFY';
  }

  const wantsToPaste =
    /\b(paste|import|add)\b.*\brecipe\b/.test(lower) ||
    /i (have|got) a recipe/.test(lower) ||
    /\b(create|make) a recipe from this\b/.test(lower) ||
    /want (to|me to) (paste|import|add)/.test(lower);

  // 5. Recipe creation from pasted content (actual content present).
  if (
    looksLikeRecipeContent(m) ||
    /\b(make|turn|structure|organize|parse|format) (this|that|the following) (into|as) a recipe\b/.test(lower) ||
    (wantsToPaste && m.length > 200)
  ) {
    return 'RECIPE_CREATION';
  }

  // 6. Recipe import ready (wants to paste, no content yet).
  if (wantsToPaste) {
    return 'RECIPE_IMPORT_READY';
  }

  // 7. Search recipes.
  if (
    /\b(do you have|find|search|looking for)\b/.test(lower) ||
    /\brecipes?\b.*\b(with|for|containing|using)\b/.test(lower) ||
    /something with/.test(lower) ||
    /any .* recipes/.test(lower)
  ) {
    return 'SEARCH_RECIPES';
  }

  // 8. Question about a specific recipe.
  if (
    /how much|how long|how many|what temperature|what ingredients|what's in|what is in/.test(lower) ||
    /tell me about (this|the)/.test(lower) ||
    /\b(servings|ingredients|substitut\w*)\b.*\?/.test(lower)
  ) {
    return 'RECIPE_QUESTION';
  }

  // 9. Open the studio.
  if (
    /open (the )?(recipe )?studio/.test(lower) ||
    /recipe (creation )?studio/.test(lower) ||
    /help me (create|make) a recipe/.test(lower) ||
    /\b(create|make) (me )?(a |new )?recipe\b/.test(lower) ||
    /\bnew recipe\b/.test(lower)
  ) {
    return 'STUDIO_OPEN';
  }

  // 10. General cooking question / chat.
  return 'GENERAL';
}
